import React, { useEffect, useCallback } from 'react'
import { node, object, string } from 'prop-types'
import { dark, light } from '../themes'
import { ThemeProvider } from 'styled-components'

const ThemeContext = React.createContext()

const ToggleThemeProvider = ({ children, dark, light, localStorageModifier, themeOverride }) => {
  const LOCAL_STORAGE_KEY = `@@tiltify/ui/theme${localStorageModifier}`
  const DEFAULT_THEME = 'dark'
  const [isDark, toggleTheme] = React.useState(!themeOverride)

  const validate = (theme) => {
    if (theme === 'dark') return 'dark'
    if (theme === 'light') return 'light'
    return 'dark'
  }

  const setInitialTheme = useCallback((theme) => {
    validate(theme) === 'dark' ? toggleTheme(true) : toggleTheme(false)
  }, [])

  const loadTheme = useCallback(() => {
    try {
      const value = window.localStorage.getItem(LOCAL_STORAGE_KEY)
      const theme = validate(value)
      setInitialTheme(theme)
      return theme
    } catch (e) {
      return validate(DEFAULT_THEME)
    }
  }, [setInitialTheme, LOCAL_STORAGE_KEY])

  useEffect(() => {
    const theme = loadTheme()
    setInitialTheme(theme)
  }, [loadTheme, setInitialTheme])

  useEffect(() => {
    if (themeOverride) toggleTheme(false)
  }, [themeOverride])

  useEffect(() => {
    const body = document.querySelector('body')

    if (!isDark) {
      body.classList.remove('dark')
      body.classList.add('light')
    } else {
      body.classList.remove('light')
      body.classList.add('dark')
    }
  }, [isDark])

  const saveTheme = (theme) => {
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, validate(theme))
    } catch (e) {
      return
    }
  }

  const toggle = () => {
    toggleTheme(!isDark)
    saveTheme(isDark ? 'light' : 'dark')
  }

  const themeContext = {
    toggle,
    isDark,
  }

  return (
    <ThemeContext.Provider value={themeContext}>
      <ThemeProvider theme={themeOverride || (isDark ? dark : light)}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  )
}

ToggleThemeProvider.propTypes = {
  children: node,
  dark: object,
  light: object,
  localStorageModifier: string,
  themeOverride: object,
}

ToggleThemeProvider.defaultProps = {
  dark: dark,
  light: light,
  localStorageModifier: '',
  themeOverride: null,
}

const useThemeContext = () => {
  const context = React.useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ToggleThemeProvider')
  }
  return context
}

export { ToggleThemeProvider, useThemeContext }
